import React, { useState, useEffect } from 'react';
import { Card, Typography, Space } from 'antd';
import { CheckCircleFilled, LoadingOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// Setup is usually near-instant, so ticking the steps off as fast as the data
// arrives produces a flash that reads as a glitch rather than progress. Each
// step is held briefly, then the finished state is held again, so the sequence
// is legible. Only ever shown for a team that was NOT ready when the screen
// opened — an existing team goes straight to its content.
const STEP_REVEAL_MS = 280;
const READY_HOLD_MS = 900;
const SLOW_AFTER_MS = 20000;

// A team is created by the client, but most of it is assembled afterwards by
// Cloud Function triggers: onCreateTeam makes the team's own client account,
// its invite code and its default tipping tag, and onCreateTeamMember writes
// the owner's membership and permissions. Until those land the page has almost
// nothing to show — and, more importantly, a team is FOUND through the members
// collection group, so it isn't really yours until your member record exists.
export function teamSetupSteps(team, teamMembers, clientId) {
  const t = team || {};
  const members = teamMembers || [];
  const you = members.find((m) => m.clientId === clientId);

  return [
    { key: 'account', label: 'Creating the team account', done: !!t.teamClientId && t.status === 'live' },
    { key: 'membership', label: 'Adding you to the team', done: members.length > 0 },
    { key: 'permissions', label: 'Applying permissions', done: !!(you && you.permissions) },
    { key: 'tag', label: 'Preparing the tipping page', done: !!t.defaultTagId },
    { key: 'invite', label: 'Generating the invite link', done: !!t.inviteUuid },
  ];
}

export function isTeamReady(team, teamMembers, clientId) {
  return teamSetupSteps(team, teamMembers, clientId).every((step) => step.done);
}

// A team is only "under construction" for as long as its triggers could still
// plausibly be running. Past that it is either finished or broken, and either
// way trapping someone behind a progress screen is the wrong answer.
const SETUP_WINDOW_MS = 5 * 60 * 1000;

const couldStillBeBuilding = (team) => {
  const createdAt = (team || {}).createdAt;
  const date = createdAt && typeof createdAt.toDate === 'function' ? createdAt.toDate() : null;
  // No createdAt yet means the client has just written the team and the trigger
  // has not stamped it — that is as new as a team gets, not an old one.
  if (!date) return true;
  return (Date.now() - date.getTime()) < SETUP_WINDOW_MS;
};

/**
 * Whether to show the setup sequence at all.
 *
 * Skip it when the team is already built:
 *   1. setupCompletedAt — written by onCreateTeam once everything is in place.
 *      Only on teams created after that field was added.
 *   2. Every checklist item present — covers every existing team.
 *
 * Otherwise show it, unless the team is old enough that setup clearly is not
 * running any more — a legacy team missing a field it never had must not sit on
 * a progress screen forever.
 *
 * Callers must not ask until the data has actually loaded — an unloaded members
 * list looks identical to a team that has not been built yet.
 */
export function shouldShowTeamSetup(team, teamMembers, clientId) {
  if (!team || team.setupCompletedAt) return false;
  if (isTeamReady(team, teamMembers, clientId)) return false;
  return couldStillBeBuilding(team);
}

/**
 * Shown while a newly created team is still being assembled. Each step is
 * derived live from Firestore, but revealed on a short timer so the sequence is
 * visible rather than flashing past. Calls `onFinished` once every step has been
 * shown green and the "Team ready" state has been held for a beat.
 */
export default function TeamUnderConstruction({ team, teamMembers, clientId, teamName, onFinished }) {
  const steps = teamSetupSteps(team, teamMembers, clientId);
  const completedCount = steps.filter((step) => step.done).length;

  const [revealed, setRevealed] = useState(0);
  const [slow, setSlow] = useState(false);
  const allRevealed = revealed >= steps.length;

  // Walk the ticks forward one at a time, never ahead of the real data.
  useEffect(() => {
    if (revealed >= completedCount) return undefined;
    const timer = setTimeout(() => setRevealed((r) => r + 1), STEP_REVEAL_MS);
    return () => clearTimeout(timer);
  }, [revealed, completedCount]);

  // Hold the finished state briefly so it registers, then hand over.
  useEffect(() => {
    if (!allRevealed || !onFinished) return undefined;
    const timer = setTimeout(onFinished, READY_HOLD_MS);
    return () => clearTimeout(timer);
  }, [allRevealed, onFinished]);

  // A trigger that never fires is a real failure mode; say so rather than spin.
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 1rem' }}>
      <Card style={{ width: '100%', maxWidth: 520, borderRadius: 8 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {allRevealed ? `${teamName || 'Your team'} is ready` : `Setting up ${teamName || 'your team'}`}
            </Title>
            <Text type="secondary">
              {allRevealed
                ? 'Opening your team…'
                : "This usually takes a few seconds. It'll open on its own when it's ready."}
            </Text>
          </div>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {steps.map((step, index) => {
              const shown = index < revealed;
              return (
                <Space key={step.key} align="center">
                  {shown
                    ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                    : <LoadingOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
                  <Text type={shown ? undefined : 'secondary'}>{step.label}</Text>
                </Space>
              );
            })}
          </Space>

          {allRevealed && (
            <div style={{ textAlign: 'center', paddingTop: 8 }}>
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: 48 }} />
              <Title level={5} style={{ marginTop: 12, marginBottom: 0 }}>Team ready</Title>
            </div>
          )}

          {slow && !allRevealed && (
            <Text type="warning">
              This is taking longer than usual. The team is safe — try refreshing, and
              contact support if it doesn't finish.
            </Text>
          )}
        </Space>
      </Card>
    </div>
  );
}
